// React Context for Procurement Management System

import React, { createContext, useContext, useReducer, useEffect, useState, useMemo, ReactNode } from 'react';
import { 
  User, Vendor, SKU, PurchaseOrder, QualityInspection, Invoice, VendorInvoice, POSentToVendor, HandoverLog, mockStorageLocations
} from '@/data/mockData';
import { MockAPIService, ApiResponse } from '@/services/mockAPI';
import { getCurrentUser, saveCurrentUser, clearCurrentUser, StorageManager } from '@/utils/localStorage';
import { toast } from 'sonner';

// State interface
export interface ProcurementState {
  // Authentication
  currentUser: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  
  // Data
  skus: SKU[];
  vendors: Vendor[];
  purchaseOrders: PurchaseOrder[];
  qualityInspections: QualityInspection[];
  invoices: Invoice[];
  vendorInvoices: VendorInvoice[];
  posSentToVendors: POSentToVendor[];
  handoverLogs: HandoverLog[];
  storageLocations: string[];
  users: User[];
  
  // UI State
  dashboardStats: any;
  filters: {
    skus: any;
    vendors: any;
    purchaseOrders: any;
  };
  
  // Error handling
  error: string | null;
}

// Action types
export type ProcurementAction =
  // Authentication actions
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGOUT' }
  
  // SKU actions
  | { type: 'SET_SKUS'; payload: SKU[] }
  | { type: 'ADD_SKU'; payload: SKU }
  | { type: 'UPDATE_SKU'; payload: { id: number; data: Partial<SKU> } }
  | { type: 'DELETE_SKU'; payload: number }
  
  // Vendor actions
  | { type: 'SET_VENDORS'; payload: Vendor[] }
  | { type: 'ADD_VENDOR'; payload: Vendor }
  | { type: 'UPDATE_VENDOR'; payload: { id: number; data: Partial<Vendor> } }
  
  // Purchase Order actions
  | { type: 'SET_PURCHASE_ORDERS'; payload: PurchaseOrder[] }
  | { type: 'ADD_PURCHASE_ORDER'; payload: PurchaseOrder }
  | { type: 'UPDATE_PURCHASE_ORDER'; payload: { id: number; data: Partial<PurchaseOrder> } }
  | { type: 'DELETE_PURCHASE_ORDER'; payload: number }
  
  // Quality Inspection actions
  | { type: 'SET_QUALITY_INSPECTIONS'; payload: QualityInspection[] }
  | { type: 'ADD_QUALITY_INSPECTION'; payload: QualityInspection }
  
  // Invoice actions
  | { type: 'SET_INVOICES'; payload: Invoice[] }
  | { type: 'ADD_INVOICE'; payload: Invoice }
  | { type: 'UPDATE_INVOICE'; payload: { id: number; data: Partial<Invoice> } }
  | { type: 'DELETE_INVOICE'; payload: number }
  
  // Vendor Invoice actions
  | { type: 'SET_VENDOR_INVOICES'; payload: VendorInvoice[] }
  | { type: 'ADD_VENDOR_INVOICE'; payload: VendorInvoice }
  | { type: 'UPDATE_VENDOR_INVOICE'; payload: { id: number; data: Partial<VendorInvoice> } }
  
  // PO Sent to Vendor actions
  | { type: 'SET_POS_SENT_TO_VENDORS'; payload: POSentToVendor[] }
  | { type: 'ADD_PO_SENT_TO_VENDOR'; payload: POSentToVendor }
  
  // Handover Log actions
  | { type: 'SET_HANDOVER_LOGS'; payload: HandoverLog[] }
  | { type: 'ADD_HANDOVER_LOG'; payload: HandoverLog }
  | { type: 'UPDATE_HANDOVER_LOG'; payload: { id: number; data: Partial<HandoverLog> } }
  
  // User actions
  | { type: 'SET_USERS'; payload: User[] }
  
  | { type: 'SET_DASHBOARD_STATS'; payload: any }
  | { type: 'SET_FILTERS'; payload: { type: string; filters: any } };

// Initial state
const initialState: ProcurementState = {
  currentUser: getCurrentUser() || null,
  isLoading: false,
  isAuthenticated: !!getCurrentUser(),
  
  skus: [],
  vendors: [],
  purchaseOrders: [],
  qualityInspections: [],
  invoices: [],
  vendorInvoices: [],
  posSentToVendors: [],
  handoverLogs: [],
  storageLocations: mockStorageLocations,
  users: [],
  
  dashboardStats: null,
  filters: {
    skus: {},
    vendors: {},
    purchaseOrders: {}
  },
  
  error: null
};

// Reducer
const procurementReducer = (state: ProcurementState, action: ProcurementAction): ProcurementState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
      
    case 'SET_ERROR':
      return { ...state, error: action.payload };
      
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        currentUser: action.payload,
        isAuthenticated: true,
        error: null
      };
      
    case 'LOGOUT':
      return {
        ...state,
        currentUser: null,
        isAuthenticated: false,
        skus: [],
        vendors: [],
        purchaseOrders: [],
        qualityInspections: [],
        invoices: [],
        dashboardStats: null,
        error: null
      };
      
    // SKU actions
    case 'SET_SKUS':
      return { ...state, skus: action.payload };
      
    case 'ADD_SKU':
      return { ...state, skus: [...state.skus, action.payload] };
      
    case 'UPDATE_SKU':
      return {
        ...state,
        skus: state.skus.map(sku =>
          sku.id === action.payload.id
            ? { ...sku, ...action.payload.data }
            : sku
        )
      };
      
    case 'DELETE_SKU':
      return {
        ...state,
        skus: state.skus.filter(sku => sku.id !== action.payload)
      };
      
    // Vendor actions
    case 'SET_VENDORS':
      return { ...state, vendors: action.payload };
      
    case 'ADD_VENDOR':
      return { ...state, vendors: [...state.vendors, action.payload] };
      
    case 'UPDATE_VENDOR':
      return {
        ...state,
        vendors: state.vendors.map(vendor =>
          vendor.id === action.payload.id
            ? { ...vendor, ...action.payload.data }
            : vendor
        )
      };
      
    // Purchase Order actions
    case 'SET_PURCHASE_ORDERS':
      return { ...state, purchaseOrders: action.payload };
      
    case 'ADD_PURCHASE_ORDER':
      return { ...state, purchaseOrders: [...state.purchaseOrders, action.payload] };
      
    case 'UPDATE_PURCHASE_ORDER':
      return {
        ...state,
        purchaseOrders: state.purchaseOrders.map(po =>
          po.id === action.payload.id
            ? { ...po, ...action.payload.data }
            : po
        )
      };
      
    case 'DELETE_PURCHASE_ORDER':
      return {
        ...state,
        purchaseOrders: state.purchaseOrders.filter(po => po.id !== action.payload)
      };
      
    // Quality Inspection actions
    case 'SET_QUALITY_INSPECTIONS':
      return { ...state, qualityInspections: action.payload };
      
    case 'ADD_QUALITY_INSPECTION':
      return { ...state, qualityInspections: [...state.qualityInspections, action.payload] };
      
    // Invoice actions
    case 'SET_INVOICES':
      return { ...state, invoices: action.payload };
      
    case 'ADD_INVOICE':
      return { ...state, invoices: [...state.invoices, action.payload] };
      
    case 'UPDATE_INVOICE':
      return {
        ...state,
        invoices: state.invoices.map(invoice =>
          invoice.id === action.payload.id
            ? { ...invoice, ...action.payload.data, updatedAt: new Date().toISOString() }
            : invoice
        )
      };
      
    case 'DELETE_INVOICE':
      return {
        ...state,
        invoices: state.invoices.filter(invoice => invoice.id !== action.payload)
      };
      
    // Vendor Invoice actions
    case 'SET_VENDOR_INVOICES':
      return { ...state, vendorInvoices: action.payload };
      
    case 'ADD_VENDOR_INVOICE':
      return { ...state, vendorInvoices: [...state.vendorInvoices, action.payload] };
      
    case 'UPDATE_VENDOR_INVOICE':
      return {
        ...state,
        vendorInvoices: state.vendorInvoices.map(invoice =>
          invoice.id === action.payload.id
            ? { ...invoice, ...action.payload.data }
            : invoice
        )
      };
      
    // PO Sent to Vendor actions
    case 'SET_POS_SENT_TO_VENDORS':
      return { ...state, posSentToVendors: action.payload };
      
    case 'ADD_PO_SENT_TO_VENDOR':
      return { ...state, posSentToVendors: [...state.posSentToVendors, action.payload] };
      
    // Handover Log actions
    case 'SET_HANDOVER_LOGS':
      return { ...state, handoverLogs: action.payload };
      
    case 'ADD_HANDOVER_LOG':
      return { ...state, handoverLogs: [...state.handoverLogs, action.payload] };
      
    case 'UPDATE_HANDOVER_LOG':
      return {
        ...state,
        handoverLogs: state.handoverLogs.map(log =>
          log.id === action.payload.id
            ? { ...log, ...action.payload.data }
            : log
        )
      };
      
    // User actions
    case 'SET_USERS':
      return { ...state, users: action.payload };
      
    case 'SET_DASHBOARD_STATS':
      return { ...state, dashboardStats: action.payload };
      
    case 'SET_FILTERS':
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.payload.type]: action.payload.filters
        }
      };
      
    default:
      return state;
  }
};

// Context
const ProcurementContext = createContext<{
  state: ProcurementState;
  dispatch: React.Dispatch<ProcurementAction>;
  actions: ProcurementActions;
} | null>(null);

// Actions interface
export interface ProcurementActions {
  // Authentication
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  
  // SKU Management
  fetchSKUs: (filters?: any) => Promise<void>;
  createSKU: (skuData: any) => Promise<boolean>;
  updateSKU: (id: number, skuData: any) => Promise<boolean>;
  deleteSKU: (id: number) => Promise<boolean>;
  
  // Vendor Management
  fetchVendors: (filters?: any) => Promise<void>;
  createVendor: (vendorData: any) => Promise<boolean>;
  updateVendor: (id: number, vendorData: any) => Promise<boolean>;
  
  // Purchase Order Management
  fetchPurchaseOrders: (filters?: any) => Promise<void>;
  createPurchaseOrder: (poData: any) => Promise<boolean>;
  updatePurchaseOrder: (id: number, poData: any) => Promise<boolean>;
  deletePurchaseOrder: (id: number) => Promise<boolean>;
  
  // PO Approval Workflow
  approvePurchaseOrder: (id: number, approvedBy: number) => Promise<boolean>;
  rejectPurchaseOrder: (id: number, rejectedBy: number, remarks?: string) => Promise<boolean>;
  requestPurchaseOrderChanges: (id: number, requestedBy: number, remarks: string) => Promise<boolean>;
  
  // Quality Inspections
  fetchQualityInspections: (filters?: any) => Promise<void>;
  createQualityInspection: (inspectionData: any) => Promise<boolean>;
  
  // Invoices
  fetchInvoices: (filters?: any) => Promise<void>;
  createInvoice: (invoiceData: any) => Promise<boolean>;
  updateInvoice: (id: number, invoiceData: any) => Promise<boolean>;
  deleteInvoice: (id: number) => Promise<boolean>;
  
  // Vendor Invoices
  fetchVendorInvoices: (filters?: any) => Promise<void>;
  createVendorInvoice: (invoiceData: any) => Promise<boolean>;
  updateVendorInvoice: (id: number, invoiceData: any) => Promise<boolean>;
  verifyVendorInvoice: (id: number) => Promise<boolean>;
  payVendorInvoice: (id: number) => Promise<boolean>;
  
  // PO Sent to Vendors
  fetchPOSentToVendors: (filters?: any) => Promise<void>;
  sendPOToVendor: (poId: number, vendorId: number, deliveryMethod: string, emailAddress?: string, notes?: string) => Promise<boolean>;
  fixSentToVendorStatus: () => boolean;
  
  // Handover Logs
  fetchHandoverLogs: (filters?: any) => Promise<void>;
  createHandoverLog: (handoverData: any) => Promise<boolean>;
  updateHandoverLog: (id: number, handoverData: any) => Promise<boolean>;
  
  // Users
  fetchUsers: () => Promise<void>;
  
  // PO Approval
  approvePO: (poId: number, action: string, notes?: string) => Promise<boolean>;
  
  // Invoice Verification
  verifyInvoice: (invoiceId: number, verified: boolean, notes?: string) => Promise<boolean>;
  
  // IQC Testing
  completeIQCTesting: (poId: number, testResults: any) => Promise<boolean>;
  
  // Quality Inspections
  updateQualityInspection: (id: number, inspectionData: any) => Promise<boolean>;
  deleteQualityInspection: (id: number) => Promise<boolean>;
  
  // Dashboard
  fetchDashboardStats: () => Promise<void>;
  
  // Utility
  setFilters: (type: string, filters: any) => void;
  clearError: () => void;
  refreshMockData: () => Promise<void>;
  clearAllData: () => Promise<void>;
  hasSavedData: () => boolean;
  getStorageStats: () => any;
}

// Provider component
interface ProcurementProviderProps {
  children: ReactNode;
}

export const ProcurementProvider: React.FC<ProcurementProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(procurementReducer, initialState);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Debug log to verify provider is mounting
  useEffect(() => {
    console.log('ProcurementProvider mounted');
    return () => console.log('ProcurementProvider unmounting');
  }, []);

  // Helper function to handle API responses
  const handleApiResponse = <T,>(
    response: ApiResponse<T>,
    successMessage?: string,
    showToast: boolean = true
  ): T | null => {
    try {
      if (!response) {
        console.error('handleApiResponse: response is null or undefined');
        dispatch({ type: 'SET_ERROR', payload: 'No response received from server' });
        return null;
      }

      if (response.success) {
        if (successMessage && showToast) {
          toast.success(successMessage);
        }
        dispatch({ type: 'SET_ERROR', payload: null });
        return response.data || null;
      } else {
        const errorMessage = response.message || 'An error occurred';
        console.error('API Error:', errorMessage);
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        if (showToast) {
          toast.error(errorMessage);
        }
        return null;
      }
    } catch (error) {
      console.error('handleApiResponse error:', error);
      const errorMsg = 'An unexpected error occurred';
      dispatch({ type: 'SET_ERROR', payload: errorMsg });
      if (showToast) {
        toast.error(errorMsg);
      }
      return null;
    }
  };

  // Actions implementation
  const actions: ProcurementActions = useMemo(() => ({
    // Authentication
    login: async (username: string, password: string): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.login(username, password);
        const user = handleApiResponse(response, 'Login successful');
        
        if (user) {
          saveCurrentUser(user);
          dispatch({ type: 'LOGIN_SUCCESS', payload: user });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Login failed' });
        toast.error('Login failed');
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    logout: () => {
      clearCurrentUser();
      dispatch({ type: 'LOGOUT' });
      toast.success('Logged out successfully');
    },

    // SKU Management
    fetchSKUs: async (filters = {}) => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        // Check if we already have SKUs in state (loaded from localStorage)
        if (state.skus.length > 0) {
          // We already have data, no need to fetch from API
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
        
        // Only fetch from API if we don't have any data
        const response = await MockAPIService.getSKUs(filters);
        const skus = handleApiResponse(response, undefined, false);
        
        if (skus) {
          dispatch({ type: 'SET_SKUS', payload: skus });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch SKUs' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    createSKU: async (skuData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.createSKU(skuData);
        const newSKU = handleApiResponse(response, 'SKU created successfully');
        
        if (newSKU) {
          dispatch({ type: 'ADD_SKU', payload: newSKU });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create SKU' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    updateSKU: async (id: number, skuData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.updateSKU(id, skuData);
        const updatedSKU = handleApiResponse(response, 'SKU updated successfully');
        
        if (updatedSKU) {
          dispatch({ type: 'UPDATE_SKU', payload: { id, data: updatedSKU } });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update SKU' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    deleteSKU: async (id: number): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.deleteSKU(id);
        const success = handleApiResponse(response, 'SKU deleted successfully');
        
        if (success) {
          dispatch({ type: 'DELETE_SKU', payload: id });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to delete SKU' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Vendor Management
    fetchVendors: async (filters = {}) => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        // Check if we already have vendors in state (loaded from localStorage)
        if (state.vendors.length > 0) {
          // We already have data, no need to fetch from API
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        const response = await MockAPIService.getVendors(filters);
        const vendors = handleApiResponse(response, undefined, false);
        
        if (vendors) {
          dispatch({ type: 'SET_VENDORS', payload: vendors });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch vendors' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    createVendor: async (vendorData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.createVendor(vendorData);
        const newVendor = handleApiResponse(response, 'Vendor created successfully');
        
        if (newVendor) {
          dispatch({ type: 'ADD_VENDOR', payload: newVendor });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create vendor' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    updateVendor: async (id: number, vendorData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.updateVendor(id, vendorData);
        const updatedVendor = handleApiResponse(response, 'Vendor updated successfully');
        
        if (updatedVendor) {
          dispatch({ type: 'UPDATE_VENDOR', payload: { id, data: updatedVendor } });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update vendor' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Purchase Orders
    fetchPurchaseOrders: async (filters = {}) => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        // Check if we already have POs in state (loaded from localStorage)
        if (state.purchaseOrders.length > 0) {
          // We already have data, no need to fetch from API
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        const response = await MockAPIService.getPurchaseOrders(filters);
        const pos = handleApiResponse(response, undefined, false);
        
        if (pos) {
          dispatch({ type: 'SET_PURCHASE_ORDERS', payload: pos });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch purchase orders' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    createPurchaseOrder: async (poData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.createPurchaseOrder(poData);
        const newPO = handleApiResponse(response, 'Purchase order created successfully');
        
        if (newPO) {
          dispatch({ type: 'ADD_PURCHASE_ORDER', payload: newPO });
          
          // Update SKU price history with the most recent purchase prices
          if (newPO.items && newPO.items.length > 0) {
            newPO.items.forEach((poItem: any) => {
              const sku = state.skus.find(s => s.id === poItem.skuId);
              if (sku && poItem.unitPrice > 0) {
                // Generate unique ID for price history entry
                const maxId = Math.max(...(sku.priceHistory || []).map(ph => ph.id), 0);
                
                // Create new price history entry
                const newPriceEntry = {
                  id: maxId + 1,
                  vendorId: newPO.vendorId,
                  price: poItem.unitPrice,
                  date: newPO.createdAt || new Date().toISOString(),
                  changedBy: newPO.createdBy || 1,
                  reason: 'New Purchase Order',
                  poId: newPO.id,
                  quantity: poItem.quantity
                };
                
                // Update SKU with new price history (append, don't overwrite)
                const updatedPriceHistory = [...(sku.priceHistory || []), newPriceEntry];
                
                 // Debug logging
                 console.log('Updating SKU price history:', {
                   skuId: sku.id,
                   skuName: sku.name,
                   oldPriceHistory: sku.priceHistory?.length || 0,
                   newPriceEntry,
                   updatedPriceHistory: updatedPriceHistory.length,
                   poItem: poItem
                 });
                
                // Update the SKU's current price and last purchase price
                dispatch({
                  type: 'UPDATE_SKU',
                  payload: {
                    id: sku.id,
                    data: {
                      lastPurchasePrice: poItem.unitPrice,
                      priceHistory: updatedPriceHistory,
                      updatedAt: new Date().toISOString()
                    }
                  }
                });
              }
            });
          }
          
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create purchase order' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    updatePurchaseOrder: async (id: number, poData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.updatePurchaseOrder(id, poData);
        const updatedPO = handleApiResponse(response, 'Purchase order updated successfully');
        
        if (updatedPO) {
          dispatch({ type: 'UPDATE_PURCHASE_ORDER', payload: { id, data: updatedPO } });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update purchase order' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    deletePurchaseOrder: async (id: number): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.deletePurchaseOrder(id);
        const success = handleApiResponse(response, 'Purchase order deleted successfully');
        
        if (success) {
          dispatch({ type: 'DELETE_PURCHASE_ORDER', payload: id });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to delete purchase order' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // PO Approval Workflow
    approvePurchaseOrder: async (id: number, approvedBy: number): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const approvalData = {
          status: 'APPROVED' as const,
          approvedBy,
          approvedAt: new Date().toISOString(),
          needsRevision: false,
          approvalRemarks: undefined
        };
        
        const response = await MockAPIService.updatePurchaseOrder(id, approvalData);
        const updatedPO = handleApiResponse(response, 'Purchase order approved successfully');
        
        if (updatedPO) {
          dispatch({ type: 'UPDATE_PURCHASE_ORDER', payload: { id, data: updatedPO } });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to approve purchase order' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    rejectPurchaseOrder: async (id: number, rejectedBy: number, remarks?: string): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const rejectionData = {
          status: 'REJECTED' as const,
          rejectedBy,
          rejectedAt: new Date().toISOString(),
          rejectionReason: remarks,
          approvalRemarks: remarks,
          needsRevision: false
        };
        
        const response = await MockAPIService.updatePurchaseOrder(id, rejectionData);
        const updatedPO = handleApiResponse(response, 'Purchase order rejected successfully');
        
        if (updatedPO) {
          dispatch({ type: 'UPDATE_PURCHASE_ORDER', payload: { id, data: updatedPO } });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to reject purchase order' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    requestPurchaseOrderChanges: async (id: number, requestedBy: number, remarks: string): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const changeRequestData = {
          status: 'NEEDS_REVISION' as const,
          needsRevision: true,
          approvalRemarks: remarks,
          rejectedBy: requestedBy,
          rejectedAt: new Date().toISOString()
        };
        
        const response = await MockAPIService.updatePurchaseOrder(id, changeRequestData);
        const updatedPO = handleApiResponse(response, 'Change request submitted successfully');
        
        if (updatedPO) {
          dispatch({ type: 'UPDATE_PURCHASE_ORDER', payload: { id, data: updatedPO } });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to request changes' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Quality Inspections
    fetchQualityInspections: async (filters = {}) => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        // Check if we already have inspections in state (loaded from localStorage)
        if (state.qualityInspections.length > 0) {
          // We already have data, no need to fetch from API
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        const response = await MockAPIService.getQualityInspections(filters);
        const inspections = handleApiResponse(response, undefined, false);
        
        if (inspections) {
          dispatch({ type: 'SET_QUALITY_INSPECTIONS', payload: inspections });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch quality inspections' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    createQualityInspection: async (inspectionData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.createQualityInspection(inspectionData);
        const newInspection = handleApiResponse(response, 'Quality inspection created successfully');
        
        if (newInspection) {
          dispatch({ type: 'ADD_QUALITY_INSPECTION', payload: newInspection });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create quality inspection' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Invoices
    fetchInvoices: async (filters = {}) => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.getInvoices(filters);
        const invoices = handleApiResponse(response, undefined, false);
        
        if (invoices) {
          dispatch({ type: 'SET_INVOICES', payload: invoices });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch invoices' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Users
    fetchUsers: async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        // Check if we already have users in state (loaded from localStorage)
        if (state.users.length > 0) {
          // We already have data, no need to fetch from API
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        const response = await MockAPIService.getUsers();
        const users = handleApiResponse(response, undefined, false);
        
        if (users) {
          dispatch({ type: 'SET_USERS', payload: users });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch users' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Handover Logs
    fetchHandoverLogs: async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.getHandoverLogs();
        const handoverLogs = handleApiResponse(response, undefined, false);
        
        if (handoverLogs) {
          dispatch({ type: 'SET_HANDOVER_LOGS', payload: handoverLogs });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch handover logs' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    createHandoverLog: async (handoverData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.createHandoverLog(handoverData);
        const newHandoverLog = handleApiResponse(response, 'Handover log created successfully');
        
        if (newHandoverLog) {
          dispatch({ type: 'ADD_HANDOVER_LOG', payload: newHandoverLog });
          return true;
        }
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create handover log' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    updateHandoverLog: async (id: number, handoverData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.updateHandoverLog(id, handoverData);
        const updatedHandoverLog = handleApiResponse(response, 'Handover log updated successfully');
        
        if (updatedHandoverLog) {
          dispatch({ type: 'UPDATE_HANDOVER_LOG', payload: { id, data: handoverData } });
          return true;
        }
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update handover log' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    createInvoice: async (invoiceData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.createInvoice(invoiceData);
        const newInvoice = handleApiResponse(response, 'Invoice created successfully');
        
        if (newInvoice) {
          dispatch({ type: 'ADD_INVOICE', payload: newInvoice });
          
          // Update PO status based on total invoiced quantity
          if (invoiceData.poNumber && invoiceData._shouldUpdatePOStatus !== undefined) {
            const po = state.purchaseOrders.find(p => p.poNumber === invoiceData.poNumber);
            if (po) {
              // Check if PO should be marked as fully received
              const shouldMarkAsFullyReceived = invoiceData._shouldUpdatePOStatus;
              
              dispatch({ 
                type: 'UPDATE_PURCHASE_ORDER', 
                payload: { 
                  id: po.id, 
                  data: { 
                    status: shouldMarkAsFullyReceived ? 'FULLY_RECEIVED' : 'PARTLY_RECEIVED',
                    updatedAt: new Date().toISOString()
                  } 
                } 
              });
              
              // Update SKU stock levels based on invoiced quantity (not full PO quantity)
              if (shouldMarkAsFullyReceived) {
                po.items.forEach(poItem => {
                  const sku = state.skus.find(s => s.id === poItem.skuId);
                  if (sku) {
                    const newStock = sku.currentStock + poItem.quantity;
                    dispatch({
                      type: 'UPDATE_SKU',
                      payload: {
                        id: sku.id,
                        data: {
                          currentStock: newStock,
                          updatedAt: new Date().toISOString()
                        }
                      }
                    });
                  }
                });
              }
            }
          }
          
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create invoice' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Dashboard
    fetchDashboardStats: async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.getDashboardStats();
        const stats = handleApiResponse(response, undefined, false);
        
        if (stats) {
          dispatch({ type: 'SET_DASHBOARD_STATS', payload: stats });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch dashboard stats' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Utility
    setFilters: (type: string, filters: any) => {
      dispatch({ type: 'SET_FILTERS', payload: { type, filters } });
    },

    clearError: () => {
      dispatch({ type: 'SET_ERROR', payload: null });
    },

    // Refresh mock data
    refreshMockData: async () => {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('Refreshing mock data...');
        }
        const response = await MockAPIService.refreshMockData();
        if (process.env.NODE_ENV === 'development') {
          console.log('Refresh response:', response);
        }
        if (response.success) {
          // Reload all data after refresh
          if (process.env.NODE_ENV === 'development') {
            console.log('Reloading data after refresh...');
          }
          await Promise.all([
            actions.fetchSKUs(),
            actions.fetchVendors(),
            actions.fetchPurchaseOrders(),
            actions.fetchQualityInspections(),
            actions.fetchInvoices(),
            actions.fetchUsers()
          ]);
          if (process.env.NODE_ENV === 'development') {
            console.log('Data reloaded successfully');
          }
          toast.success('Mock data refreshed successfully');
        } else {
          console.error('Failed to refresh mock data:', response.message);
          toast.error('Failed to refresh mock data');
        }
      } catch (error) {
        console.error('Error refreshing mock data:', error);
        toast.error('Failed to refresh mock data');
      }
    },

    // Clear all data and reset to defaults
    clearAllData: async () => {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('Clearing all data...');
        }
        StorageManager.clearAll();
        
        // Reset state to initial values
        dispatch({ type: 'SET_SKUS', payload: [] });
        dispatch({ type: 'SET_VENDORS', payload: [] });
        dispatch({ type: 'SET_PURCHASE_ORDERS', payload: [] });
        dispatch({ type: 'SET_QUALITY_INSPECTIONS', payload: [] });
        dispatch({ type: 'SET_INVOICES', payload: [] });
        dispatch({ type: 'SET_USERS', payload: [] });
        
        // Reload with fresh mock data
        await Promise.all([
          actions.fetchSKUs(),
          actions.fetchVendors(),
          actions.fetchPurchaseOrders(),
          actions.fetchQualityInspections(),
          actions.fetchInvoices(),
          actions.fetchUsers()
        ]);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('All data cleared and reset');
        }
        toast.success('All data cleared and reset to defaults');
      } catch (error) {
        console.error('Error clearing data:', error);
        toast.error('Failed to clear data');
      }
    },

    // Check if data exists in localStorage
    hasSavedData: () => {
      return StorageManager.exists('SKUS') || 
             StorageManager.exists('VENDORS') || 
             StorageManager.exists('PURCHASE_ORDERS') ||
             StorageManager.exists('USERS');
    },

    // Get storage statistics
    getStorageStats: () => {
      return StorageManager.getStorageStats();
    },

    // Invoice Management
    updateInvoice: async (id: number, invoiceData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        console.log('ProcurementContext - Updating invoice:', id, 'with data:', invoiceData);
        const response = await MockAPIService.updateInvoice(id, invoiceData);
        const success = handleApiResponse(response, 'Invoice updated successfully');
        
        if (success) {
          console.log('ProcurementContext - Invoice update successful, updating local state');
          // Update local state immediately
          dispatch({ 
            type: 'UPDATE_INVOICE', 
            payload: { 
              id, 
              data: { ...invoiceData, updatedAt: new Date().toISOString() }
            } 
          });
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('ProcurementContext - Failed to update invoice:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update invoice' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    deleteInvoice: async (id: number): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.deleteInvoice(id);
        const success = handleApiResponse(response, 'Invoice deleted successfully');
        
        if (success) {
          // Refresh invoices to get updated data
          await actions.fetchInvoices();
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to delete invoice' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // PO Approval
    approvePO: async (poId: number, action: string, notes?: string): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.approvePO(poId, action, notes, state.currentUser?.id);
        const success = handleApiResponse(response, response.message);
        
        if (success) {
          // Update the PO in the state directly instead of refetching
          const updatedPOs = state.purchaseOrders.map(po => 
            po.id === poId 
              ? { 
                  ...po, 
                  status: (action === 'approve' ? 'APPROVED' : action === 'reject' ? 'REJECTED' : 'NEEDS_REVISION') as 'APPROVED' | 'REJECTED' | 'NEEDS_REVISION',
                  updatedAt: new Date().toISOString(),
                  notes: notes ? `${po.notes || ''}\nApproval Notes: ${notes}`.trim() : po.notes
                }
              : po
          );
          dispatch({ type: 'SET_PURCHASE_ORDERS', payload: updatedPOs });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to approve PO' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Invoice Verification
    verifyInvoice: async (invoiceId: number, verified: boolean, notes?: string): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.verifyInvoice(invoiceId, verified, notes, state.currentUser?.id);
        const success = handleApiResponse(response, response.message);
        
        if (success) {
          // Update invoices state directly
          const updatedInvoices = state.invoices.map(invoice => 
            invoice.id === invoiceId 
              ? { 
                  ...invoice, 
                  status: (verified ? 'Approved' : 'Rejected') as 'Approved' | 'Rejected',
                  verificationNotes: notes,
                  updatedAt: new Date().toISOString()
                }
              : invoice
          );
          dispatch({ type: 'SET_INVOICES', payload: updatedInvoices });
          
          // If verified, update related PO to APPROVED
          if (verified) {
            const invoice = state.invoices.find(i => i.id === invoiceId);
            if (invoice) {
              const updatedPOs = state.purchaseOrders.map(po => 
                po.id === invoice.poId 
                  ? { 
                      ...po, 
                      status: 'APPROVED' as const,
                      securityVerified: true,
                      securityVerifiedBy: state.currentUser?.id || 1,
                      securityVerifiedAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString()
                    }
                  : po
              );
              dispatch({ type: 'SET_PURCHASE_ORDERS', payload: updatedPOs });
            }
          }
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to verify invoice' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // IQC Testing
    completeIQCTesting: async (poId: number, testResults: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.completeIQCTesting(poId, testResults, state.currentUser?.id);
        const success = handleApiResponse(response, response.message);
        
        if (success) {
          // Calculate overall status based on test results
          let overallStatus = '';
          const allItems = testResults.items;
          const fullyAccepted = allItems.every((item: any) => item.rejectedQty === 0);
          const fullyRejected = allItems.every((item: any) => item.acceptedQty === 0);
          
          if (fullyAccepted) {
            overallStatus = 'Fully Accepted';
          } else if (fullyRejected) {
            overallStatus = 'Fully Rejected';
          } else {
            overallStatus = 'Partly Accepted';
          }

          // Update PO with IQC results
          const updatedItems = testResults.items.map((item: any) => ({
            ...item,
            iqcTested: true,
            iqcTestedBy: state.currentUser?.id || 1,
            iqcTestedAt: new Date().toISOString(),
            iqcNotes: item.notes || ''
          }));

          const updatedPOs = state.purchaseOrders.map(po => 
            po.id === poId 
              ? { 
                  ...po, 
                  items: updatedItems,
                  status: overallStatus as any,
                  iqcCompleted: true,
                  iqcCompletedBy: state.currentUser?.id || 1,
                  iqcCompletedAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                }
              : po
          );
          dispatch({ type: 'SET_PURCHASE_ORDERS', payload: updatedPOs });
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to complete IQC testing' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Quality Inspections
    updateQualityInspection: async (id: number, inspectionData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.updateQualityInspection(id, inspectionData);
        const success = handleApiResponse(response, 'Quality inspection updated successfully');
        
        if (success) {
          // Refresh quality inspections to get updated data
          await actions.fetchQualityInspections();
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update quality inspection' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    deleteQualityInspection: async (id: number): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.deleteQualityInspection(id);
        const success = handleApiResponse(response, 'Quality inspection deleted successfully');
        
        if (success) {
          // Refresh quality inspections to get updated data
          await actions.fetchQualityInspections();
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to delete quality inspection' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Vendor Invoices
    fetchVendorInvoices: async (filters = {}) => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        // For now, we'll use mock data directly
        // In a real app, this would be an API call
        const mockVendorInvoices = await import('@/data/mockData').then(m => m.mockVendorInvoices);
        dispatch({ type: 'SET_VENDOR_INVOICES', payload: mockVendorInvoices });
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch vendor invoices' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    createVendorInvoice: async (invoiceData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const response = await MockAPIService.createVendorInvoice(invoiceData);
        const newInvoice = handleApiResponse(response, 'Vendor invoice created successfully');
        
        if (newInvoice) {
          // Add to vendor invoices
          dispatch({ type: 'ADD_VENDOR_INVOICE', payload: newInvoice });
          
          // Also add to main invoices list so it appears in Invoice Tracking
          dispatch({ type: 'ADD_INVOICE', payload: newInvoice });
          
          console.log('Created vendor invoice:', newInvoice);
          return true;
        }
        
        return false;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create vendor invoice' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    updateVendorInvoice: async (id: number, invoiceData: any): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        dispatch({ type: 'UPDATE_VENDOR_INVOICE', payload: { id, data: invoiceData } });
        return true;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update vendor invoice' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    verifyVendorInvoice: async (id: number): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const updateData = {
          status: 'verified' as const,
          verifiedDate: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        dispatch({ type: 'UPDATE_VENDOR_INVOICE', payload: { id, data: updateData } });
        return true;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to verify vendor invoice' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    payVendorInvoice: async (id: number): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const updateData = {
          status: 'paid' as const,
          paidDate: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        dispatch({ type: 'UPDATE_VENDOR_INVOICE', payload: { id, data: updateData } });
        return true;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to pay vendor invoice' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // PO Sent to Vendors
    fetchPOSentToVendors: async (filters = {}) => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        // For now, we'll use mock data directly
        // In a real app, this would be an API call
        const mockPOSentToVendors = await import('@/data/mockData').then(m => m.mockSentPOsToVendors);
        dispatch({ type: 'SET_POS_SENT_TO_VENDORS', payload: mockPOSentToVendors });
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch PO sent to vendors' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    // Debug function to fix any remaining SENT_TO_VENDOR status issues
    fixSentToVendorStatus: () => {
      const savedPOs = StorageManager.load('PURCHASE_ORDERS', []);
      if (savedPOs && savedPOs.length > 0) {
        const updatedPOs = savedPOs.map((po: any) => {
          if (po.status === 'SENT_TO_VENDOR') {
            console.log('Fixing PO with SENT_TO_VENDOR status:', po.poNumber);
            return {
              ...po,
              status: 'APPROVED',
              sentToVendor: true,
              sentToVendorDate: po.updatedAt || new Date().toISOString()
            };
          }
          return po;
        });
        
        const hasChanges = updatedPOs.some((po: any, index: number) => 
          po.status !== savedPOs[index].status || po.sentToVendor !== savedPOs[index].sentToVendor
        );
        
        if (hasChanges) {
          StorageManager.save('PURCHASE_ORDERS', updatedPOs);
          console.log('Fixed SENT_TO_VENDOR status issues');
          return true;
        }
      }
      return false;
    },

    sendPOToVendor: async (poId: number, vendorId: number, deliveryMethod: string, emailAddress?: string, notes?: string): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const poSent = {
          id: Date.now(),
          poId,
          vendorId,
          sentDate: new Date().toISOString(),
          sentBy: state.currentUser?.id || 1,
          deliveryMethod: deliveryMethod as any,
          status: 'sent' as const,
          notes,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        dispatch({ type: 'ADD_PO_SENT_TO_VENDOR', payload: poSent });
        
        // Mark PO as sent to vendor without changing status
        dispatch({ type: 'UPDATE_PURCHASE_ORDER', payload: { 
          id: poId, 
          data: { 
            sentToVendor: true,
            sentToVendorDate: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          } 
        }});
        
        return true;
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to send PO to vendor' });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [dispatch]);

  // Load initial data when user is authenticated or for demo purposes
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        console.log('🔄 Starting data load process...');
        
        // Validate localStorage health first
        const healthCheck = StorageManager.checkHealth();
        if (!healthCheck.isHealthy) {
          console.warn('⚠️ localStorage health check failed:', healthCheck.error);
        } else {
          console.log('✅ localStorage health check passed');
        }
        
        // Clean up any existing POs with SENT_TO_VENDOR status
        const savedPOs = StorageManager.load('PURCHASE_ORDERS', []);
        if (savedPOs && savedPOs.length > 0) {
          const updatedPOs = savedPOs.map((po: any) => {
            if (po.status === 'SENT_TO_VENDOR') {
              return {
                ...po,
                status: 'APPROVED', // Reset to APPROVED status
                sentToVendor: true,
                sentToVendorDate: po.updatedAt || new Date().toISOString()
              };
            }
            return po;
          });
          
          // Only save if there were changes
          const hasChanges = updatedPOs.some((po: any, index: number) => 
            po.status !== savedPOs[index].status || po.sentToVendor !== savedPOs[index].sentToVendor
          );
          
          if (hasChanges) {
            StorageManager.save('PURCHASE_ORDERS', updatedPOs);
            console.log('🔄 Updated POs with SENT_TO_VENDOR status to use new sentToVendor flag');
          }
        }

        // First try to load from localStorage
        const savedSKUs = StorageManager.load('SKUS', []);
        const savedVendors = StorageManager.load('VENDORS', []);
        const finalSavedPOs = StorageManager.load('PURCHASE_ORDERS', []);
        const savedInspections = StorageManager.load('QUALITY_INSPECTIONS', []);
        const savedInvoices = StorageManager.load('INVOICES', []);
        const savedUsers = StorageManager.load('USERS', []);

        // Try to restore from emergency backup if main data is missing
        if (savedSKUs.length === 0 && savedVendors.length === 0 && savedPOs.length === 0 && savedUsers.length === 0) {
          const emergencyBackup = StorageManager.load('BACKUP_DATA');
          if (emergencyBackup) {
            console.log('Main data missing, attempting to restore from emergency backup...');
            const restored = StorageManager.restoreFromBackup(emergencyBackup as string);
            if (restored) {
              // Reload the data after restoration
              const restoredSKUs = StorageManager.load('SKUS', []);
              const restoredVendors = StorageManager.load('VENDORS', []);
              const restoredPOs = StorageManager.load('PURCHASE_ORDERS', []);
              const restoredInspections = StorageManager.load('QUALITY_INSPECTIONS', []);
              const restoredInvoices = StorageManager.load('INVOICES', []);
              const restoredUsers = StorageManager.load('USERS', []);
              
              savedSKUs.push(...restoredSKUs);
              savedVendors.push(...restoredVendors);
              savedPOs.push(...restoredPOs);
              savedInspections.push(...restoredInspections);
              savedInvoices.push(...restoredInvoices);
              savedUsers.push(...restoredUsers);
              
              console.log('Successfully restored from emergency backup');
            }
          }
        }

        // If we have saved data, use it
        if (savedSKUs.length > 0 || savedVendors.length > 0 || savedPOs.length > 0 || savedUsers.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Loading saved data from localStorage:', {
              skus: savedSKUs.length,
              vendors: savedVendors.length,
              pos: finalSavedPOs.length,
              inspections: savedInspections.length,
              invoices: savedInvoices.length,
              users: savedUsers.length
            });
            
            // Show detailed PO data for debugging
            console.log('📊 PO Data Details:', {
              totalPOs: finalSavedPOs.length,
              totalValue: finalSavedPOs.reduce((sum, po) => sum + (po?.totalAmount || 0), 0),
              poNumbers: finalSavedPOs.map(po => po?.poNumber).slice(0, 5), // First 5 PO numbers
              statuses: finalSavedPOs.map(po => po?.status).slice(0, 5) // First 5 statuses
            });
          }
          
          // Dispatch all saved data
          dispatch({ type: 'SET_SKUS', payload: savedSKUs });
          dispatch({ type: 'SET_VENDORS', payload: savedVendors });
          dispatch({ type: 'SET_PURCHASE_ORDERS', payload: finalSavedPOs });
          dispatch({ type: 'SET_QUALITY_INSPECTIONS', payload: savedInspections });
          dispatch({ type: 'SET_INVOICES', payload: savedInvoices });
          dispatch({ type: 'SET_USERS', payload: savedUsers });
          
          // Load additional data if available
          const savedVendorInvoices = StorageManager.load('VENDOR_INVOICES', []);
          const savedPOSent = StorageManager.load('POS_SENT_TO_VENDORS', []);
          const savedHandoverLogs = StorageManager.load('HANDOVER_LOGS', []);
          const savedDashboardStats = StorageManager.load('DASHBOARD_STATS', null);
          
          dispatch({ type: 'SET_VENDOR_INVOICES', payload: savedVendorInvoices });
          dispatch({ type: 'SET_POS_SENT_TO_VENDORS', payload: savedPOSent });
          dispatch({ type: 'SET_HANDOVER_LOGS', payload: savedHandoverLogs });
          dispatch({ type: 'SET_DASHBOARD_STATS', payload: savedDashboardStats });
          
          console.log('✅ Data loaded from localStorage successfully');
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ No saved data found, fetching from API...');
          }
          // Otherwise fetch from API
          await Promise.all([
            actions.fetchSKUs(),
            actions.fetchVendors(),
            actions.fetchPurchaseOrders(),
            actions.fetchQualityInspections(),
            actions.fetchInvoices(),
            actions.fetchUsers(),
            actions.fetchDashboardStats()
          ]);
        }
        
        // Mark initial load as complete
        setIsInitialLoad(false);
      } catch (error) {
        console.error('Failed to load initial data:', error);
        setIsInitialLoad(false);
      }
    };

    // Load data regardless of authentication status for demo purposes
    loadInitialData();
  }, []); // Empty dependency array - only run once on mount

  // Save data to localStorage whenever state changes (but not on initial load)
  useEffect(() => {
    // Only save if we're not in the initial loading phase and have actual data
    if (!isInitialLoad && !state.isLoading) {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('💾 Saving state to localStorage...');
          console.log('📊 Current State Summary:', {
            totalPOs: state.purchaseOrders.length,
            totalValue: state.purchaseOrders.reduce((sum, po) => sum + (po?.totalAmount || 0), 0),
            poNumbers: state.purchaseOrders.map(po => po?.poNumber).slice(0, 5),
            statuses: state.purchaseOrders.map(po => po?.status).slice(0, 5),
            skus: state.skus.length,
            vendors: state.vendors.length,
            users: state.users.length
          });
        }
        
        // Save all data to localStorage
        StorageManager.save('SKUS', state.skus);
        StorageManager.save('VENDORS', state.vendors);
        StorageManager.save('PURCHASE_ORDERS', state.purchaseOrders);
        StorageManager.save('QUALITY_INSPECTIONS', state.qualityInspections);
        StorageManager.save('INVOICES', state.invoices);
        StorageManager.save('USERS', state.users);
        StorageManager.save('VENDOR_INVOICES', state.vendorInvoices);
        StorageManager.save('POS_SENT_TO_VENDORS', state.posSentToVendors);
        StorageManager.save('HANDOVER_LOGS', state.handoverLogs);
        StorageManager.save('DASHBOARD_STATS', state.dashboardStats);
        
        // Auto-save complete state for recovery
        StorageManager.autoSave(state);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ State saved successfully to localStorage');
        }
      } catch (error) {
        console.error('❌ Failed to save data to localStorage:', error);
        // Try to create emergency backup even if regular save fails
        try {
          const emergencyBackup = StorageManager.createFullBackup();
          StorageManager.save('BACKUP_DATA', emergencyBackup);
          console.log('🆘 Emergency backup created due to save failure');
        } catch (backupError) {
          console.error('❌ Failed to create emergency backup:', backupError);
        }
      }
    }
  }, [isInitialLoad, state.skus, state.vendors, state.purchaseOrders, state.qualityInspections, state.invoices, state.users, state.vendorInvoices, state.posSentToVendors, state.handoverLogs, state.dashboardStats, state.isLoading]);

  // Save data when page is about to be unloaded (hard refresh, close tab, etc.)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Show current state before unload
      console.log('🔄 === BEFORE RELOAD STATE ===');
      console.log('📊 Total POs:', state.purchaseOrders.length);
      console.log('💰 Total Value:', state.purchaseOrders.reduce((sum, po) => sum + (po?.totalAmount || 0), 0));
      console.log('📋 PO Numbers:', state.purchaseOrders.map(po => po?.poNumber));
      console.log('🏷️ PO Statuses:', state.purchaseOrders.map(po => po?.status));
      console.log('📄 Vendor Invoices:', state.vendorInvoices.length);
      console.log('📤 Sent POs:', state.posSentToVendors.length);
      console.log('👥 Users:', state.users.length);
      console.log('🏢 Vendors:', state.vendors.length);
      console.log('📦 SKUs:', state.skus.length);
      console.log('🔍 Inspections:', state.qualityInspections.length);
      console.log('🧾 Invoices:', state.invoices.length);
      console.log('========================');
      
      try {
        // Save all current state data
        StorageManager.save('SKUS', state.skus);
        StorageManager.save('VENDORS', state.vendors);
        StorageManager.save('PURCHASE_ORDERS', state.purchaseOrders);
        StorageManager.save('QUALITY_INSPECTIONS', state.qualityInspections);
        StorageManager.save('INVOICES', state.invoices);
        StorageManager.save('USERS', state.users);
        StorageManager.save('VENDOR_INVOICES', state.vendorInvoices);
        StorageManager.save('POS_SENT_TO_VENDORS', state.posSentToVendors);
        StorageManager.save('DASHBOARD_STATS', state.dashboardStats);
        
        // Create a comprehensive backup before page unload
        const backup = StorageManager.createFullBackup();
        StorageManager.save('BACKUP_DATA', backup);
        console.log('✅ Emergency backup created before page unload');
      } catch (error) {
        console.error('❌ Failed to create emergency backup:', error);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is being hidden, save current state
        try {
          StorageManager.autoSave(state);
          console.log('💾 State saved due to page visibility change');
        } catch (error) {
          console.error('❌ Failed to save state on visibility change:', error);
        }
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Set up periodic auto-save (every 30 seconds)
    const autoSaveInterval = setInterval(() => {
      if (!isInitialLoad && !state.isLoading && (state.skus.length > 0 || state.vendors.length > 0 || state.purchaseOrders.length > 0)) {
        try {
          StorageManager.autoSave(state);
          console.log('⏰ Periodic auto-save completed');
        } catch (error) {
          console.error('❌ Periodic auto-save failed:', error);
        }
      }
    }, 30000); // 30 seconds

    // Cleanup event listeners and interval on unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(autoSaveInterval);
    };
  }, [state, isInitialLoad]);

  // Ensure we always provide a valid context value
  const contextValue = { state, dispatch, actions };
  
  console.log('ProcurementProvider rendering, contextValue:', !!contextValue);

  return (
    <ProcurementContext.Provider value={contextValue}>
      {children}
    </ProcurementContext.Provider>
  );
};

// Custom hook to use the context
export const useProcurement = () => {
  const context = useContext(ProcurementContext);
  console.log('useProcurement called, context:', !!context);
  if (!context) {
    console.error('ERROR: useProcurement - context is null! This component is not wrapped by ProcurementProvider');
    throw new Error('useProcurement must be used within a ProcurementProvider');
  }
  return context;
};

export default ProcurementContext;

